import { Box, Stack, Typography } from "@mui/material";
import { type FC, useEffect, useRef, useState } from "react";

import { TagChip } from "~/components/UI/CategoryChip";
import { PlatformIcons } from "~/components/UI/PlatformIcons";
import type { Platform } from "~/types/platform";
import { EventTagEnum } from "~/types/tag";

export interface MarketCellProps {
  platform: Platform;
  title: string;
  isTrending: boolean;
  isBreaking: boolean;
}

const TAG_WIDTH = 82;
const TITLE_LINE_HEIGHT = "1.42em";
const TITLE_TWO_LINES_HEIGHT = "2.84em";

type TitleLayout = "singleLine" | "inlineTag" | "truncatedTag";

const wrapSx = {
  whiteSpace: "normal",
  wordBreak: "break-word",
} as const;

const measureSx = {
  ...wrapSx,
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  visibility: "hidden",
  pointerEvents: "none",
} as const;

export const MarketCell: FC<MarketCellProps> = ({
  title,
  platform,
  isTrending,
  isBreaking,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const titleMeasureRef = useRef<HTMLElement | null>(null);
  const titleWithTagMeasureRef = useRef<HTMLElement | null>(null);
  const [layout, setLayout] = useState<TitleLayout>("singleLine");

  const hasTag = isTrending || isBreaking;

  useEffect(() => {
    const container = containerRef.current;
    const titleMeasure = titleMeasureRef.current;
    const titleWithTagMeasure = titleWithTagMeasureRef.current;

    if (!container || !titleMeasure || !titleWithTagMeasure || !hasTag) {
      return;
    }

    const update = () => {
      const lineHeight = parseFloat(getComputedStyle(titleMeasure).lineHeight);
      const titleLines = Math.round(titleMeasure.scrollHeight / lineHeight);
      const withTagLines = Math.round(
        titleWithTagMeasure.scrollHeight / lineHeight
      );

      if (titleLines <= 1) {
        setLayout("singleLine");
      } else if (withTagLines <= 2) {
        setLayout("inlineTag");
      } else {
        setLayout("truncatedTag");
      }
    };

    update();
    void document.fonts.ready.then(update);

    const observer = new ResizeObserver(update);
    observer.observe(container);

    return () => observer.disconnect();
  }, [hasTag, title]);

  const tag = (
    <TagChip tag={isBreaking ? EventTagEnum.BREAKING : EventTagEnum.TRENDING} />
  );

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        height: "100%",
        gap: 1
      }}>
      <Box>
        <PlatformIcons platforms={platform} size={20} />
      </Box>
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          minWidth: 0,
          flex: 1,
          py: "5px"
        }}>
        {hasTag && (
          <>
            <Typography
              ref={titleMeasureRef}
              aria-hidden
              variant="caption"
              sx={[{
                fontWeight: 600
              }, ...(Array.isArray(measureSx) ? measureSx : [measureSx])]}>
              {title}
            </Typography>
            <Typography
              ref={titleWithTagMeasureRef}
              aria-hidden
              variant="caption"
              sx={[{
                fontWeight: 600
              }, ...(Array.isArray(measureSx) ? measureSx : [measureSx])]}>
              {title}
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: TAG_WIDTH,
                  height: "1px",
                }}
              />
            </Typography>
          </>
        )}
        {!hasTag ? (
          <Typography
            component="h3"
            variant="caption"
            sx={{
              fontWeight: 600,
              ...wrapSx,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical"
            }}>
            {title}
          </Typography>
        ) : layout === "singleLine" ? (
          <Stack
            sx={{
              alignItems: "flex-start",
              gap: "2px"
            }}>
            <Typography
              component="h3"
              variant="caption"
              noWrap
              sx={{
                fontWeight: 600,
                maxWidth: "100%"
              }}>
              {title}
            </Typography>
            {tag}
          </Stack>
        ) : layout === "inlineTag" ? (
          <Typography
            component="h3"
            variant="caption"
            sx={[{
              fontWeight: 600
            }, ...(Array.isArray(wrapSx) ? wrapSx : [wrapSx])]}>
            {title}
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                verticalAlign: "middle",
                ml: "4px",
              }}
            >
              {tag}
            </Box>
          </Typography>
        ) : (
          <Typography
            component="h3"
            variant="caption"
            sx={{
              fontWeight: 600,
              ...wrapSx,
              overflow: "hidden",
              maxHeight: TITLE_TWO_LINES_HEIGHT
            }}>
            <Box
              component="span"
              sx={{
                float: "right",
                marginTop: TITLE_LINE_HEIGHT,
                ml: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
              }}
            >
              …{tag}
            </Box>
            {title}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
